import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { instituteToday } from '@/features/teacher/utils';
import { applyFieldErrors } from '@/lib/forms';
import { ApiRequestError } from '@/lib/apiClient';
import type { InstituteNotice } from '@/types/instituteNotice';
import { instituteNoticesApi } from '../api/instituteNotices.api';
import { AUDIENCE_HINT, AUDIENCE_OPTIONS } from '../constants';
import {
  emptyInstituteNoticeForm,
  instituteNoticeFormFrom,
  instituteNoticeFormResolver,
  instituteNoticeSchema,
  type InstituteNoticeFormValues,
} from '../schemas/instituteNotice.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface InstituteNoticeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The notice being edited. Absent means a new notice is being put up. */
  notice?: InstituteNotice | null;
  /** Gets the saved notice back, so the page can confirm it by name. */
  onPosted: (posted: InstituteNotice) => void;
}

/**
 * Putting something up on the institute's board, or rewording one already up.
 *
 * An edit is not quiet: the server audits every changed field with what it said
 * before, so "what did the notice say on Monday" still has an answer.
 */
export function InstituteNoticeFormModal({
  isOpen,
  onClose,
  notice,
  onPosted,
}: InstituteNoticeFormModalProps) {
  const isEditing = Boolean(notice);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<InstituteNoticeFormValues>({
    resolver: instituteNoticeFormResolver,
    defaultValues: emptyInstituteNoticeForm(instituteToday()),
  });
  useLiveFormErrors(form);
  const {
    formState: { isSubmitting, errors },
    register,
    watch,
  } = form;

  const audience = watch('audience');
  const body = watch('body');

  // Refill whenever the dialog opens, so a half-typed notice never appears over
  // the next one being composed — or over the one being edited.
  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    form.reset(notice ? instituteNoticeFormFrom(notice) : emptyInstituteNoticeForm(instituteToday()));
  }, [isOpen, notice, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);

    try {
      const payload = instituteNoticeSchema.parse(values);
      const posted = notice
        ? await instituteNoticesApi.update(notice.uuid, payload)
        : await instituteNoticesApi.create(payload);

      // The page raises the confirmation once this dialog has closed — a toast
      // under an open `<dialog>` sits behind it, in the browser's top layer.
      onPosted(posted);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // Field errors stay on the fields — a message that fades cannot be the
        // only place the office is told which box to fix.
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit notice' : 'Put up a notice'}
      description={
        isEditing
          ? 'Everybody it is addressed to sees the new wording the next time they open the app.'
          : 'Everybody it is addressed to sees it the next time they open the app.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} isLoading={isSubmitting}>
            {isEditing
              ? isSubmitting
                ? 'Saving…'
                : 'Save changes'
              : isSubmitting
                ? 'Posting…'
                : 'Post notice'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert
            tone="critical"
            title={isEditing ? 'Could not save this notice' : 'Could not put this notice up'}
          >
            {formError}
          </Alert>
        )}

        <Select
          label="Who it is for"
          options={AUDIENCE_OPTIONS}
          hint={audience ? AUDIENCE_HINT[audience] : undefined}
          error={errors.audience?.message}
          {...register('audience')}
        />

        <Input
          label="Title"
          placeholder="Institute closed on Monday"
          hint="The whole notice, for most notices — it is what a card shows."
          error={errors.title?.message}
          {...register('title')}
        />

        <Textarea
          label="Details (optional)"
          rows={3}
          placeholder="Local holiday declared by the district administration. Classes resume on Tuesday."
          hint={`${body?.length ?? 0} of 2000 characters used.`}
          error={errors.body?.message}
          {...register('body')}
        />

        <DatePicker
          label="Date it is about"
          hint="The day the notice concerns, which is not always today — tomorrow's notice is usually typed tonight. It must fall inside this session."
          error={errors.noticeDate?.message}
          {...register('noticeDate')}
        />
      </form>
    </Modal>
  );
}
